import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import type * as React from 'react'

import { cn } from './cn'

/**
 * The marketing site's button (`HomeButton`, the dialog kit): every button
 * is a pill. Primary and destructive paint a gradient inside a 1.5px ink
 * edge; outline and secondary sit on a 1px ring over the surface. Tokens
 * live in globals.css (`--button-*`).
 */
const SURFACE =
  'border border-(--button-ring) bg-(--button-surface) text-foreground shadow-(--button-shadow) hover:bg-(--button-surface-hover)'

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium text-sm tracking-[-0.01em] outline-none transition focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          'border-(--button-edge) border-[1.5px] bg-brand-gradient text-primary-foreground shadow-(--button-raised) hover:brightness-105',
        destructive:
          'border-(--button-edge) border-[1.5px] bg-danger-gradient text-white shadow-(--button-raised) hover:brightness-105 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        outline: SURFACE,
        secondary: SURFACE,
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        outlineSecondary: cn(SURFACE, 'text-secondary-foreground'),
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 has-[>svg]:px-3.5',
        xs: "h-6 gap-1 px-2.5 text-caption has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 px-3.5 has-[>svg]:px-3',
        lg: 'h-10 px-5 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export type { ButtonProps }
export { Button, buttonVariants }
